"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UNMET_REASONS = exports.suppressionOverrideInputSchema = exports.attemptRoutingSchema = exports.routingViewSchema = exports.routingResolutionSchema = exports.routingStepSchema = exports.routingRecipientSchema = void 0;
/**
 * `RPM-070` — an alert's routing as the workup reads it: the latest resolution, its steps and where
 * each stands, the obligation, and whether the routing is due to resolve again (`J-284`).
 *
 * RECIPIENT VALUES ARE THE BUILDING'S CONFIGURATION, NOT PHI, AND TRAVEL IN BODIES ONLY — never in a
 * path, a query string or a log line (the block's API row).
 */
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
exports.routingRecipientSchema = zod_1.z.object({
    value: zod_1.z.string(),
    label: zod_1.z.string(),
    channel: zod_1.z.enum(["EMAIL", "PHONE"]),
    extension: zod_1.z.string().nullable(),
});
exports.routingStepSchema = zod_1.z.object({
    order: zod_1.z.number().int(),
    method: zod_1.z.enum(["CALL", "EMAIL", "TEXT", "DAILY_SUMMARY", "EMR_ONLY"]),
    trigger: zod_1.z.enum(["ALWAYS", "ON_NO_ANSWER"]),
    due: zod_1.z.enum(["NOW", "AFTER_TWO_UNANSWERED_CALLS", "AT_NEXT_DAILY_SUMMARY", "NONE"]),
    carriesNote: zod_1.z.boolean(),
    contents: zod_1.z.array(zod_1.z.string()),
    to: zod_1.z.array(exports.routingRecipientSchema),
    cc: zod_1.z.array(exports.routingRecipientSchema),
    /** Where the step stands after the attempts logged so far (the block's Operator surface row). */
    state: zod_1.z.enum(["OWED", "WAITING", "DISCHARGED", "UNMET", "EXHAUSTED", "NOT_NEEDED"]),
    /** An `UNMET` step's reason, from `RPM-035`'s closed list. */
    stateReason: zod_1.z.string().nullable(),
    /**
     * The outcomes logged against this step, oldest first (`RPM-042`, `J-230`). `REACHED`, `TEXTED` and
     * `EMAILED` end the step; a `NO_ANSWER` is here and the step is still owed. A sent summary writes
     * no attempt, so a summary step's discharge is its state, not a line here.
     */
    logged: zod_1.z.array(zod_1.z.enum(rpm_enums_js_1.attemptOutcome.enumValues)),
    /** The email a suppression override added (`RPM-046`). */
    override: zod_1.z.boolean(),
    /**
     * `RPM-041`'s API row: on a call step, the unanswered calls counted toward it, what exhausted it,
     * and whether the step the routing marks on no answer is owed now. Null on every other step.
     */
    noAnswer: zod_1.z
        .object({
        unanswered: zod_1.z.number().int().min(0),
        /** How many unanswered calls exhaust a call step: two, counted and never timed (D8). */
        of: zod_1.z.literal(2),
        exhaustedBy: zod_1.z.enum(["TWO_NO_ANSWERS", "NO_NUMBER_REACHABLE"]).nullable(),
        fallbackStep: zod_1.z.number().int().nullable(),
        fallbackOwed: zod_1.z.boolean(),
    })
        .nullable(),
});
exports.routingResolutionSchema = zod_1.z.object({
    routingResolutionId: zod_1.z.uuid(),
    sequenceNo: zod_1.z.number().int(),
    resolvedAt: zod_1.z.iso.datetime(),
    lane: zod_1.z.enum(rpm_enums_js_1.alertLane.enumValues),
    /** The building's wall clock when it resolved, and the segment of the day. */
    clock: zod_1.z.object({
        zone: zod_1.z.string(),
        date: zod_1.z.string(),
        hour: zod_1.z.number().int(),
        minute: zod_1.z.number().int(),
        segment: zod_1.z.enum(["WEEKEND", "DAY_9_5", "NIGHT"]),
    }),
    status: zod_1.z.enum(rpm_enums_js_1.routingResolutionStatus.enumValues),
    /** Every reason that applies, in step order, comma-joined (`RPM-035`). */
    reason: zod_1.z.string().nullable(),
    holdReason: zod_1.z.string().nullable(),
    behaviour: zod_1.z.string().nullable(),
    /** The building's routing notes, verbatim. */
    instruction: zod_1.z.string(),
    steps: zod_1.z.array(exports.routingStepSchema),
    /** Whether the building's behaviour suppresses this alert's real-time email (`RPM-046`). */
    suppressed: zod_1.z.boolean(),
});
exports.routingViewSchema = zod_1.z
    .object({
    latest: exports.routingResolutionSchema.nullable(),
    obligation: zod_1.z.object({
        status: zod_1.z.enum(rpm_enums_js_1.obligationStatus.enumValues),
        unmetReason: zod_1.z.string().nullable(),
        /**
         * `RPM-016`'s API row: where the obligation is `UNMET` or `NONE`, its reason code — the unmet
         * reasons, `FACILITY_CHARTS_TO_EMR_ONLY`, or `LANE_CLEAR_NOT_ROUTED` / `LANE_MACHINE_NOT_ROUTED`
         * for a lane that is not routed. Null while it is `OWED` or `MET`.
         */
        reason: zod_1.z.string().nullable(),
        /**
         * The building's daily summary this alert goes in, `HH:MM` on its wall clock, where a summary
         * step is in the routing: "in the 17:00 daily summary" (`RPM-016`'s Operator surface row).
         */
        summaryTime: zod_1.z.string().nullable(),
    }),
    /** True when the panel must resolve before anything else is logged. */
    resolutionDue: zod_1.z.boolean(),
    /** Whether the nurse has overridden a suppression on this alert (`RPM-046`). */
    overridden: zod_1.z.boolean(),
})
    .meta({
    id: "AlertRouting",
    description: "An alert's routing, its obligation, and whether it is due to resolve",
});
/** An attempt logged against a step names the resolution and the step (`RPM-070`, rule 9). */
exports.attemptRoutingSchema = zod_1.z.object({
    routingResolutionId: zod_1.z.uuid(),
    routingStep: zod_1.z.number().int().min(1),
});
/**
 * `RPM-046`'s API row: `POST /api/rpm/v1/alerts/:id/suppression-override` with `{ channel, reason }`.
 * Email is the one channel a suppression holds back; the reason is required at the contract, so an
 * empty one is `400` before anything is read (the Blocking row).
 */
exports.suppressionOverrideInputSchema = zod_1.z
    .strictObject({
    channel: zod_1.z.literal("EMAIL"),
    reason: zod_1.z.string().trim().min(1).max(500),
})
    .meta({ description: "Override a suppressed email for this alert, with the stated reason" });
/**
 * `RPM-035`'s closed list of reasons, each in the words the routing panel shows (`J-281`). The
 * configuration reasons also say what an administrator must fix and the screen that fixes it;
 * `CALL_NOT_ANSWERED` and `NO_RECIPIENT_FOR_CALL` are facts about this alert and the moment it was
 * worked, so nothing is to be fixed.
 */
exports.UNMET_REASONS = {
    ROUTING_NOT_SET_UP: {
        words: "How this building is contacted is not set up.",
        fix: "An administrator must set the building's routing.",
        fixAt: "/admin/facility-routing",
    },
    ROUTING_HELD: {
        words: "How this building is contacted is held for review.",
        fix: "An administrator must review the building's routing and release the hold.",
        fixAt: "/admin/facility-routing",
    },
    NO_RECIPIENT_FOR_EMAIL: {
        words: "Nobody in this building's directory takes an alert email for this alert.",
        fix: "An administrator must add an alert email for the building or this care unit.",
        fixAt: "/admin/routing-directory",
    },
    NO_RECIPIENT_FOR_TEXT: {
        words: "Nobody in this building's directory takes a text for this alert.",
        fix: "An administrator must add a voice or text number for the building or this care unit.",
        fixAt: "/admin/routing-directory",
    },
    NO_RECIPIENT_FOR_DAILY_SUMMARY: {
        words: "Nobody in this building's directory receives the daily summary.",
        fix: "An administrator must add a summary or alert email for the building.",
        fixAt: "/admin/routing-directory",
    },
    NO_RECIPIENT_FOR_CALL: {
        words: "No number in this building's directory can be called now.",
        fix: null,
        fixAt: null,
    },
    SUMMARY_TIME_NOT_SET: {
        words: "This building sends concerning alerts in a daily summary, and no summary time is set.",
        fix: "An administrator must set the building's daily summary times.",
        fixAt: "/admin/facility-routing",
    },
    CALL_NOT_ANSWERED: {
        words: "The call went unanswered twice, and this building has no step after it.",
        fix: null,
        fixAt: null,
    },
};
//# sourceMappingURL=rpm-alert-routing.js.map